import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_ROLES = [
  { id: 'doctor', label: 'Doctor' },
  { id: 'hygienist', label: 'Hygienist' },
  { id: 'assistant', label: 'Assistant' },
  { id: 'front_office', label: 'Front Office' },
  { id: 'office_manager', label: 'Office Mgr' },
];

const FILTER_ALERT_TYPES_BASE = [
  { id: 'patient_arrived', label: 'Pt. Arrived' },
  { id: 'patient_seated', label: 'Pt. Seated' },
  { id: 'dr_needed', label: 'Dr. Needed' },
  { id: 'dr_in_room', label: 'Dr. In Room' },
  { id: 'help_needed', label: 'Help Needed' },
];

export function RoleBasedFilterSection({ darkMode, practice, customButtons }) {
  const filterAlertTypes = [
    ...FILTER_ALERT_TYPES_BASE,
    ...customButtons.map(btn => ({ id: `custom_btn_${btn.id}`, label: btn.name }))
  ];

  const defaultRoleFilters = () => {
    const f = {};
    FILTER_ROLES.forEach(r => {
      f[r.id] = {};
      filterAlertTypes.forEach(a => { f[r.id][a.id] = true; });
    });
    f['doctor']['dr_in_room'] = false;
    f['hygienist']['dr_needed'] = false;
    f['hygienist']['dr_in_room'] = false;
    return f;
  };

  const [roleFilterEnabled, setRoleFilterEnabled] = useState(false);
  const [roleFilterExpanded, setRoleFilterExpanded] = useState(false);
  const [roleFilters, setRoleFilters] = useState(defaultRoleFilters);

  useEffect(() => {
    if (practice?.role_based_filters) {
      setRoleFilterEnabled(practice.role_based_filters.enabled || false);
      if (practice.role_based_filters.filters) {
        setRoleFilters(prev => {
          const merged = { ...prev };
          for (const role of Object.keys(practice.role_based_filters.filters)) {
            merged[role] = { ...merged[role], ...practice.role_based_filters.filters[role] };
          }
          return merged;
        });
      }
    }
  }, [practice]);

  return (
    <div className={`glass-panel rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRoleFilterExpanded(!roleFilterExpanded)}
            className={`p-1 rounded transition-transform ${roleFilterExpanded ? 'rotate-0' : '-rotate-90'}`}
            data-testid="role-filter-expand"
          >
            <ChevronDown className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
          <h2 className={`text-xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Role Based Filtering</h2>
        </div>
        <button
          onClick={async () => {
            const newVal = !roleFilterEnabled;
            setRoleFilterEnabled(newVal);
            try {
              await axios.put(`${API}/practice/settings`, {
                role_based_filters: { enabled: newVal, filters: roleFilters }
              });
              toast.success(newVal ? 'Role-based filtering enabled' : 'Role-based filtering disabled');
            } catch {
              toast.error('Failed to update');
              setRoleFilterEnabled(!newVal);
            }
          }}
          data-testid="toggle-role-filtering"
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
            roleFilterEnabled ? 'bg-dp-600' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')
          }`}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${
            roleFilterEnabled ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      <p className={`text-sm mt-3 ${darkMode ? 'text-slate-500' : 'text-slate-500'} italic`}>
        We have optimized alerts to filter in a way that only shows relevant alerts to each user. If you desire more control, turn on Role Based Filtering for more customization.
      </p>

      {roleFilterExpanded && (
        <div className="mt-4">
          <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Control which alert types each role can see. Green = visible, Red = hidden.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="role-filter-matrix">
              <thead>
                <tr>
                  <th className={`text-left text-xs font-semibold py-2 px-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Role</th>
                  {filterAlertTypes.map(at => (
                    <th key={at.id} className={`text-center text-xs font-semibold py-2 px-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {at.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FILTER_ROLES.map(role => (
                  <tr key={role.id} className={`border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <td className={`py-3 px-3 text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      {role.label}
                    </td>
                    {filterAlertTypes.map(at => {
                      const isOn = roleFilters[role.id]?.[at.id] !== false;
                      return (
                        <td key={at.id} className="text-center py-3 px-2">
                          <button
                            data-testid={`role-filter-${role.id}-${at.id}`}
                            onClick={() => {
                              setRoleFilters(prev => ({
                                ...prev,
                                [role.id]: { ...prev[role.id], [at.id]: !isOn }
                              }));
                            }}
                            className={`w-5 h-5 rounded-full mx-auto transition-all shadow-sm ${
                              isOn ? 'bg-emerald-500 shadow-emerald-500/40' : 'bg-red-500 shadow-red-500/40'
                            }`}
                            style={{
                              boxShadow: isOn
                                ? '0 0 8px rgba(16, 185, 129, 0.5)'
                                : '0 0 8px rgba(239, 68, 68, 0.5)'
                            }}
                            title={`${role.label} - ${at.label}: ${isOn ? 'Visible' : 'Hidden'}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            data-testid="save-role-filters-btn"
            onClick={async () => {
              try {
                await axios.put(`${API}/practice/settings`, {
                  role_based_filters: { enabled: roleFilterEnabled, filters: roleFilters }
                });
                toast.success('Role-based filters saved');
              } catch { toast.error('Failed to save filters'); }
            }}
            className={`mt-4 w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              darkMode ? 'bg-dp-600 hover:bg-dp-500 text-white' : 'bg-dp-600 hover:bg-dp-700 text-white'
            }`}
          >
            Save Role Filters
          </button>
          <button
            data-testid="reset-role-filters-btn"
            onClick={async () => {
              const baseline = defaultRoleFilters();
              setRoleFilters(baseline);
              try {
                await axios.put(`${API}/practice/settings`, {
                  role_based_filters: { enabled: roleFilterEnabled, filters: baseline }
                });
                toast.success('Filters reset to native baseline');
              } catch { toast.error('Failed to reset filters'); }
            }}
            className={`mt-2 w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
            }`}
          >
            Reset to Baseline
          </button>
        </div>
      )}
    </div>
  );
}
